import React, { useContext, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import {
	ApButtonIcon,
	ApLoaderDefault,
	ApLoaderText,
	ApMenu,
	ApMenuItem,
	colors,
} from '@alixpartners/ui-components';
import { EXPORT_OPTIONS } from '../../Legal/utils';
import {
	Header,
	LoadingContainer,
	NoData,
	SectionWrapper,
	Title,
	TooltipContainer,
	TooltipItem,
	TooltipItemName,
	TooltipItemTitle,
	TooltipItemValue,
} from './Charts.styles';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import useFetchData from '../../../../../Hooks/useFetchData';
import { getCostPerLawyerGrowth } from '../../../../../utils/apiRequests';
import { renderToString } from 'react-dom/server';
import { formatNumberWithDecimals } from '../../../../../utils/number';
import { SettingsContext } from '../../../../../contexts/SettingsContext';
import { FONT_STYLE, PAGE_SIZE } from '../../../../utils';
import Pagination from '../../../../../components/Pagination/Pagination';

const CostPerLawyer = ({ chartTitle }) => {
	const { data, isLoading } = useFetchData(getCostPerLawyerGrowth);

	const { hideDecimals } = useContext(SettingsContext);
	const chartRef = useRef();
	const [currentPage, setCurrentPage] = useState(1);

	const paginatedData = useMemo(
		() => {
			if (!data?.data || data.data.length === 0) return null;

			const allRevenueData = data.data[0].revenue;
			const startIndex = (currentPage - 1) * PAGE_SIZE;
			const endIndex = startIndex + PAGE_SIZE;

			return {
				revenue: allRevenueData.slice(startIndex, endIndex),
				median: data.data[0].median,
			};
		},
		[data, currentPage]
	);

	const chartOptions = useMemo(
		() => {
			if (!paginatedData) return null;

			const firmNames = paginatedData.revenue.map(item => item.firm_name);
			const cagrValues = paginatedData.revenue.map(item => item.cagr);
			const medianValue = paginatedData.median;

			return {
				chart: {
					type: 'line',
				},
				exporting: { enabled: false, allowHTML: true },
				credits: { enabled: false },
				title: { text: '' },
				xAxis: {
					categories: firmNames,
					lineColor: colors.black,
					scrollbar: {
						enabled: true,
					},
					title: {
						text: 'Firms',
						style: { ...FONT_STYLE },
					},
					labels: {
						// formatter: function() {
						// 	if (typeof this.value !== 'number') {
						// 		if (this.value === undefined) {
						// 			return '';
						// 		} else {
						// 			if (this.value.length > 0) {
						// 				return `${this.value.substring(0, 4)}...`;
						// 			}
						// 		}
						// 	}
						// },
						style: { ...FONT_STYLE },
					},
					gridLineWidth: 0,
				},
				yAxis: {
					title: {
						text: 'CAGR (%)',
						style: { ...FONT_STYLE },
					},
					gridLineWidth: 0,
					lineColor: colors.black,
					lineWidth: 1,
				},
				tooltip: {
					shared: true,
					useHTML: true,
					borderWidth: 0,
					backgroundColor: colors.white,
					outside: true,
					borderRadius: 2,
					formatter: function() {
						return renderToString(
							<TooltipContainer>
								<TooltipItemTitle>{this.x}</TooltipItemTitle>
								{this.points.map(item => (
									<TooltipItem
										key={`${item.point.series.name}-${item.point.y}`}
									>
										<span>
											<TooltipItemName>
												{item.point.series.name}
											</TooltipItemName>
										</span>
										<TooltipItemValue>
											{formatNumberWithDecimals(item.point.y, hideDecimals)}
										</TooltipItemValue>
									</TooltipItem>
								))}
							</TooltipContainer>
						);
					},
				},
				series: [
					{
						name: 'CAGR',
						data: cagrValues,
						color: '#032E45',
					},
					{
						name: 'Median',
						data: Array(cagrValues.length).fill(medianValue),
						dashStyle: 'Dash',
						color: colors.amber400,
					},
				],
			};
		},
		[paginatedData, hideDecimals]
	);

	const handleChartExport = option => {
		const chartInstance = chartRef?.current?.chart;
		if (chartInstance && option.value === 'png') {
			chartInstance.exportChart(
				{
					filename: chartTitle,
				},
				{
					chart: { width: 1200 },
				}
			);
		}
		if (option.value === 'xlsx') {
			this.handleChartDataExport();
		}
	};

	return (
		<SectionWrapper>
			<Header>
				<Title>{chartTitle}</Title>
				<ApMenu button={<ApButtonIcon iconName="download" />}>
					{EXPORT_OPTIONS.map(option => (
						<ApMenuItem
							key={option.value}
							onClick={() => handleChartExport(option)}
						>
							{option.label}
						</ApMenuItem>
					))}
				</ApMenu>
			</Header>
			{isLoading ? (
				<LoadingContainer>
					<ApLoaderDefault loaderText={() => <ApLoaderText />} />
				</LoadingContainer>
			) : (
				<>
					{!paginatedData ? (
						<NoData>No data available</NoData>
					) : (
						<>
							<HighchartsReact
								highcharts={Highcharts}
								options={chartOptions}
								ref={chartRef}
							/>
							<Pagination
								currentPage={currentPage}
								totalCount={data?.data?.[0]?.revenue?.length || 0}
								pageSize={PAGE_SIZE}
								onPageChange={page => setCurrentPage(page)}
								position="center"
							/>
						</>
					)}
				</>
			)}
		</SectionWrapper>
	);
};

CostPerLawyer.propTypes = {
	chartTitle: PropTypes.string.isRequired,
};

export default CostPerLawyer;
